<template>
  <div style="display: contents">
    <template v-for="node in nodes">
      <!-- Fila grupo (disciplina / subdisciplina) -->
      <tr
        v-if="!isStage(node)"
        :key="'grp-' + node.id"
        class="weekly-qty-group-row"
        :class="{ 'weekly-qty-group-row--nested': depth > 0 }"
        :style="{ backgroundColor: groupBg() }"
      >
        <td
          class="py-1.5 fs-11-5 font-bold uppercase sticky-left weekly-qty-name-cell"
          :style="{
            color: depth > 0 ? '#2d5a40' : '#1e4d33',
            backgroundColor: groupBg(),
            borderRight: '1px solid #d3e2d3',
            zIndex: 1,
            paddingLeft: namePadding,
            paddingRight: '0.75rem',
          }"
        >
          <button
            v-if="canExpand(node)"
            type="button"
            class="weekly-qty-expand mr-1 inline-flex align-middle"
            @click.stop="$emit('toggle', node.id)"
          >
            <b-icon
              pack="fas"
              :icon="isExpanded(node.id) ? 'chevron-down' : 'chevron-right'"
              size="is-small"
            />
          </button>
          <span v-else class="inline-block w-4 mr-1" />
          {{ node.name }}
        </td>
        <td class="px-2 py-1.5 text-center fs-10 text-gray-400" style="border-right: 1px solid #d3e2d3">—</td>
        <td class="px-2 py-1 text-right fs-11 text-gray-300" :style="{ backgroundColor: groupBg(), borderRight: '2px solid #c3d6c3' }">—</td>
        <template v-for="w in weeks">
          <td
            v-if="!isOpen(w)"
            :key="'gcol-' + node.id + '-' + w"
            style="background-color: #dce9e1; border-right: 1px solid #cbdcd2"
          />
          <template v-else>
            <td class="sem-num font-bold" :key="'gef-' + node.id + '-' + w" :style="numStyle(groupWeek(node, w).ef, false, discBg(w))">{{ fmt(groupWeek(node, w).ef) }}</td>
            <td class="sem-num font-bold" :key="'gplan-' + node.id + '-' + w" :style="numStyle(null, false, discBg(w))">—</td>
            <td class="sem-num font-bold" :key="'gprog-' + node.id + '-' + w" :style="numStyle(null, false, discBg(w))">—</td>
            <td class="sem-num font-bold" :key="'greal-' + node.id + '-' + w" :style="numStyle(null, false, discBg(w))">—</td>
            <td class="sem-num font-bold" :key="'ghhplan-' + node.id + '-' + w" :style="numStyle(groupWeek(node, w).hhPlan, COL, discBg(w))">{{ fmt(groupWeek(node, w).hhPlan) }}</td>
            <td class="sem-num font-bold" :key="'ghhdisp-' + node.id + '-' + w" :style="numStyle(groupWeek(node, w).hhDisp, false, discBg(w))">{{ fmt(groupWeek(node, w).hhDisp) }}</td>
            <td class="sem-num font-bold" :key="'ghhprog-' + node.id + '-' + w" :style="numStyle(groupWeek(node, w).hhProg, COL, discBg(w))">{{ fmt(groupWeek(node, w).hhProg) }}</td>
            <td
              class="sem-num font-bold"
              :key="'ghhreal-' + node.id + '-' + w"
              :style="numStyle(groupWeek(node, w).hhReal, hhRealColor(groupWeek(node, w)), discBg(w))"
            >{{ fmt(groupWeek(node, w).hhReal) }}</td>
          </template>
        </template>
      </tr>

      <!-- Fila etapa -->
      <tr
        v-else
        :key="'st-' + node.id"
        class="border-t"
        style="border-color: #eef1f5"
      >
        <td
          class="py-1 fs-11 text-gray-700 sticky-left bg-white weekly-qty-name-cell"
          :class="{ 'weekly-qty-name-cell--nested': depth > 0 }"
          :style="{ borderRight: '1px solid #eef1f5', zIndex: 1, paddingLeft: namePadding, paddingRight: '0.75rem' }"
        >
          {{ node.name }}
        </td>
        <td class="px-2 py-1 text-center fs-10 text-gray-500" style="border-right: 1px solid #eef1f5">
          {{ unitLabel(node) }}
        </td>
        <td class="px-1 py-0.5 text-right" style="border-right: 2px solid #d4dae3">
          <span class="weekly-qty-factor-k">
            <input
              type="number"
              min="0"
              step="0.01"
              :value="adjustedIndexFor(node)"
              :disabled="!!node.savingAdjustedIndex"
              class="w-14 rounded border px-1 py-0.5 text-right fs-11 tabular-nums outline-none"
              style="border-color: #c7d2e3"
              @input="onAdjustedIndexInput(node, $event)"
            >
            <span
              v-if="isAdjustedIndexDirty(node)"
              class="weekly-qty-factor-k__actions"
            >
              <button
                type="button"
                class="weekly-qty-factor-k__btn weekly-qty-factor-k__btn--save"
                :disabled="!!node.savingAdjustedIndex"
                :title="$t('save')"
                @click="saveAdjustedIndex(node)"
              >
                <b-icon pack="fas" icon="check" size="is-small" />
              </button>
              <button
                type="button"
                class="weekly-qty-factor-k__btn weekly-qty-factor-k__btn--cancel"
                :disabled="!!node.savingAdjustedIndex"
                :title="$t('cancel')"
                @click="cancelAdjustedIndex(node)"
              >
                <b-icon pack="fas" icon="times" size="is-small" />
              </button>
            </span>
          </span>
        </td>

        <template v-for="w in weeks">
          <td
            v-if="!isOpen(w)"
            :key="'scol-' + node.id + '-' + w"
            style="background-color: #eef4f0; border-right: 1px solid #e0e9e3"
          />
          <template v-else-if="w >= semanaActual">
            <td class="px-1 py-0.5 text-right" :key="'sef-' + node.id + '-' + w" :style="{ backgroundColor: stageBg(w), borderRight: '1px solid #e5e9f0' }">
              <span class="weekly-qty-factor-k">
                <input
                  type="number"
                  min="0"
                  step="any"
                  :value="valsFor(w, node).ef != null ? valsFor(w, node).ef : ''"
                  :disabled="isSavingWeek(node, w)"
                  class="w-full sem-input rounded border px-1 py-0.5 text-right fs-11 tabular-nums outline-none"
                  style="border-color: #c7d2e3; background-color: #fff"
                  @input="onWeeklyInput(node, w, 'assigned_people', $event)"
                >
                <span
                  v-if="isWeeklyFieldDirty(node, w, 'assigned_people')"
                  class="weekly-qty-factor-k__actions"
                >
                  <button
                    type="button"
                    class="weekly-qty-factor-k__btn weekly-qty-factor-k__btn--save"
                    :disabled="isSavingWeek(node, w)"
                    :title="$t('save')"
                    @click="saveWeeklyWeek(node, w)"
                  >
                    <b-icon pack="fas" icon="check" size="is-small" />
                  </button>
                  <button
                    type="button"
                    class="weekly-qty-factor-k__btn weekly-qty-factor-k__btn--cancel"
                    :disabled="isSavingWeek(node, w)"
                    :title="$t('cancel')"
                    @click="cancelWeeklyField(node, w, 'assigned_people')"
                  >
                    <b-icon pack="fas" icon="times" size="is-small" />
                  </button>
                </span>
              </span>
            </td>
            <td class="sem-num" :key="'splan-' + node.id + '-' + w" :style="numStyle(valsFor(w, node).plan, false, stageBg(w))">{{ fmt(valsFor(w, node).plan) }}</td>
            <td class="px-1 py-0.5 text-right" :key="'sprog-' + node.id + '-' + w" :style="{ backgroundColor: stageBg(w), borderRight: '1px solid #e5e9f0' }">
              <span class="weekly-qty-factor-k">
                <input
                  type="number"
                  min="0"
                  step="any"
                  :value="valsFor(w, node).prog != null ? valsFor(w, node).prog : ''"
                  :disabled="isSavingWeek(node, w)"
                  class="w-full sem-input rounded border px-1 py-0.5 text-right fs-11 tabular-nums outline-none"
                  style="border-color: #c7d2e3; background-color: #fff"
                  @input="onWeeklyInput(node, w, 'scheduled_quantity', $event)"
                >
                <span
                  v-if="isWeeklyFieldDirty(node, w, 'scheduled_quantity')"
                  class="weekly-qty-factor-k__actions"
                >
                  <button
                    type="button"
                    class="weekly-qty-factor-k__btn weekly-qty-factor-k__btn--save"
                    :disabled="isSavingWeek(node, w)"
                    :title="$t('save')"
                    @click="saveWeeklyWeek(node, w)"
                  >
                    <b-icon pack="fas" icon="check" size="is-small" />
                  </button>
                  <button
                    type="button"
                    class="weekly-qty-factor-k__btn weekly-qty-factor-k__btn--cancel"
                    :disabled="isSavingWeek(node, w)"
                    :title="$t('cancel')"
                    @click="cancelWeeklyField(node, w, 'scheduled_quantity')"
                  >
                    <b-icon pack="fas" icon="times" size="is-small" />
                  </button>
                </span>
              </span>
            </td>
            <td class="sem-num" :key="'sreal-' + node.id + '-' + w" :style="numStyle(valsFor(w, node).real, false, stageBg(w))">{{ fmt(valsFor(w, node).real) }}</td>
            <td class="sem-num" :key="'shhplan-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhPlan, COL, stageBg(w))">{{ fmt(hhFor(w, node).hhPlan) }}</td>
            <td class="sem-num" :key="'shhdisp-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhDisp, false, stageBg(w))">{{ fmt(hhFor(w, node).hhDisp) }}</td>
            <td class="sem-num" :key="'shhprog-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhProg, COL, stageBg(w))">{{ fmt(hhFor(w, node).hhProg) }}</td>
            <td class="sem-num" :key="'shhreal-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhReal, hhRealColor(hhFor(w, node)), stageBg(w))">{{ fmt(hhFor(w, node).hhReal) }}</td>
          </template>
          <template v-else>
            <td class="sem-num" :key="'spef-' + node.id + '-' + w" :style="numStyle(valsFor(w, node).ef, false, null)">{{ fmt(valsFor(w, node).ef) }}</td>
            <td class="sem-num" :key="'spplan-' + node.id + '-' + w" :style="numStyle(valsFor(w, node).plan, false, null)">{{ fmt(valsFor(w, node).plan) }}</td>
            <td class="sem-num" :key="'spprog-' + node.id + '-' + w" :style="numStyle(valsFor(w, node).prog, false, null)">{{ fmt(valsFor(w, node).prog) }}</td>
            <td class="sem-num" :key="'spreal-' + node.id + '-' + w" :style="numStyle(valsFor(w, node).real, false, null)">{{ fmt(valsFor(w, node).real) }}</td>
            <td class="sem-num" :key="'sphhplan-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhPlan, COL, null)">{{ fmt(hhFor(w, node).hhPlan) }}</td>
            <td class="sem-num" :key="'sphhdisp-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhDisp, false, null)">{{ fmt(hhFor(w, node).hhDisp) }}</td>
            <td class="sem-num" :key="'sphhprog-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhProg, COL, null)">{{ fmt(hhFor(w, node).hhProg) }}</td>
            <td class="sem-num" :key="'sphhreal-' + node.id + '-' + w" :style="numStyle(hhFor(w, node).hhReal, '#2e9e5b', null)">{{ fmt(hhFor(w, node).hhReal) }}</td>
          </template>
        </template>
      </tr>

      <tr
        v-if="canExpand(node) && isExpanded(node.id) && isLoading(node.id)"
        :key="'load-' + node.id"
      >
        <td colspan="100" class="px-4 py-3 text-center text-gray-500 fs-11">
          {{ $t('loading') }}...
        </td>
      </tr>

      <weekly-quantities-tree-rows
        v-else-if="canExpand(node) && isExpanded(node.id)"
        :key="'ch-' + node.id"
        :project-id="projectId"
        :nodes="childrenByParent[node.id] || []"
        :depth="depth + 1"
        :expanded-ids="expandedIds"
        :weeks="weeks"
        :semana-actual="semanaActual"
        :open-prev="openPrev"
        :calc="calc"
        :hh-semana="hhSemana"
        :mes-inicio="mesInicio"
        :project-weeks="projectWeeks"
        :week-start-date="weekStartDate"
        :week-end-date="weekEndDate"
        :weekly-api-params="weeklyApiParams"
        :col="COL"
        :cur-bg="CUR_BG"
        :row-d-bg="ROW_D_BG"
        @toggle="$emit('toggle', $event)"
        @children-loaded="$emit('children-loaded', $event)"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import {
  applyWeeklyManHoursFromApi,
  budgetIndices,
  canExpandNode,
  groupWeekFromApi,
  isStageNode,
  mapBoqRootToWeeklyNode,
  nodeTotals,
  plannedQuantityForWeek,
  actualQuantityForWeek,
  rowKeyForNode,
  weekHhFromApi,
} from './takeoffWeeklyNodes.js'

const COL = '#0f766e'
const ROW_D_BG = '#e4efe4'
const CUR_BG = '#f4faf7'

function fmt(n) {
  if (n == null || !Number.isFinite(n)) return '—'
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5,
  })
}

export default {
  name: 'weekly-quantities-tree-rows',

  components: {
    WeeklyQuantitiesTreeRows: null,
  },

  beforeCreate() {
    this.$options.components.WeeklyQuantitiesTreeRows = this.$options
  },

  props: {
    projectId: { type: [Number, String], required: true },
    nodes: { type: Array, default: () => [] },
    depth: { type: Number, default: 0 },
    expandedIds: { type: Array, default: () => [] },
    weeks: { type: Array, default: () => [] },
    semanaActual: { type: Number, required: true },
    openPrev: { type: Object, default: () => ({}) },
    calc: { type: Object, required: true },
    hhSemana: { type: Number, default: 0 },
    mesInicio: { type: String, default: '' },
    projectWeeks: { type: Array, default: () => [] },
    weekStartDate: { type: String, default: null },
    weekEndDate: { type: String, default: null },
    weeklyApiParams: { type: Object, default: () => ({}) },
    col: { type: String, default: COL },
    curBg: { type: String, default: CUR_BG },
    rowDBg: { type: String, default: ROW_D_BG },
  },

  emits: ['toggle', 'children-loaded'],

  data() {
    return {
      COL: this.col || COL,
      CUR_BG: this.curBg || CUR_BG,
      ROW_D_BG: this.rowDBg || ROW_D_BG,
      childrenByParent: {},
      loadingParentIds: [],
      loadedParentIds: [],
    }
  },

  computed: {
    namePadding() {
      // Base + sangría moderada por nivel (subdisciplina / stage anidados)
      return `${0.75 + this.depth * 1.25}rem`
    },
  },

  watch: {
    expandedIds: {
      deep: true,
      immediate: true,
      handler(ids) {
        this.nodes.forEach((node) => {
          if (this.canExpand(node) && ids.includes(node.id)) {
            this.ensureChildren(node.id)
          }
        })
      },
    },
    weeklyApiParams: {
      deep: true,
      handler() {
        this.childrenByParent = {}
        this.loadedParentIds = []
        this.loadingParentIds = []
      },
    },
  },

  methods: {
    fmt,
    isStage: isStageNode,
    canExpand: canExpandNode,
    rowKey: rowKeyForNode,
    totals: nodeTotals,

    isExpanded(id) {
      return this.expandedIds.includes(id)
    },
    isLoading(id) {
      return this.loadingParentIds.includes(id)
    },
    isOpen(w) {
      return w === this.semanaActual || !!this.openPrev[w]
    },
    unitLabel(node) {
      const unit = node.takeoffStats && node.takeoffStats.unit
      return unit || '—'
    },
    indices(node) {
      return budgetIndices(node)
    },
    adjustedIndexFor(node) {
      const value = this.indices(node).indiceAj
      return value != null ? value : ''
    },
    parseAdjustedIndex(value) {
      if (value === '' || value == null) {
        return null
      }
      const n = Number(value)
      if (!Number.isFinite(n) || n < 0) {
        return null
      }
      return Math.round(n * 100000) / 100000
    },
    isAdjustedIndexDirty(node) {
      const current = this.parseAdjustedIndex(node.takeoffStats && node.takeoffStats.adjusted_man_hour_index)
      const saved = this.parseAdjustedIndex(node.savedAdjustedIndex)
      return current !== saved
    },
    onAdjustedIndexInput(node, event) {
      if (!node.takeoffStats) {
        this.$set(node, 'takeoffStats', {})
      }
      this.$set(node.takeoffStats, 'adjusted_man_hour_index', this.parseAdjustedIndex(event.target.value))
    },
    cancelAdjustedIndex(node) {
      if (!node.takeoffStats) {
        this.$set(node, 'takeoffStats', {})
      }
      this.$set(node.takeoffStats, 'adjusted_man_hour_index', this.parseAdjustedIndex(node.savedAdjustedIndex))
    },
    async saveAdjustedIndex(node) {
      if (!this.projectId || node.savingAdjustedIndex || !this.isAdjustedIndexDirty(node)) {
        return
      }
      this.$set(node, 'savingAdjustedIndex', true)
      try {
        const { data } = await axios.patch(
          `/api/v2/project/${this.projectId}/boq-roots/${node.id}`,
          { adjusted_man_hour_index: this.parseAdjustedIndex(node.takeoffStats && node.takeoffStats.adjusted_man_hour_index) },
        )
        if (!(data && data.success)) {
          throw new Error('save_failed')
        }
        const saved = this.parseAdjustedIndex(data.boq_root && data.boq_root.adjusted_man_hour_index)
        if (!node.takeoffStats) {
          this.$set(node, 'takeoffStats', {})
        }
        this.$set(node.takeoffStats, 'adjusted_man_hour_index', saved)
        this.$set(node, 'savedAdjustedIndex', saved)
        this.$notify.success('save_successful')
      } catch (error) {
        this.$notify.error('error_saving')
      } finally {
        this.$set(node, 'savingAdjustedIndex', false)
      }
    },
    weekDraft(node, week) {
      return (node.weeklyDraft && node.weeklyDraft[week]) || {}
    },
    weekSaved(node, week) {
      return (node.savedWeekly && node.savedWeekly[week]) || {}
    },
    valsFor(week, node) {
      const draft = this.weekDraft(node, week)
      // FTD planif. / FTD real vienen del API (solo stages).
      return {
        plan: plannedQuantityForWeek(node, week),
        ef: draft.assigned_people != null ? Number(draft.assigned_people) : null,
        prog: draft.scheduled_quantity != null ? Number(draft.scheduled_quantity) : null,
        real: actualQuantityForWeek(node, week),
      }
    },
    parseWeekValue(value) {
      if (value === '' || value == null) {
        return null
      }
      const n = Number(value)
      if (!Number.isFinite(n) || n < 0) {
        return null
      }
      return n
    },
    isSavingWeek(node, week) {
      return !!(node.savingWeekly && node.savingWeekly[week])
    },
    isWeeklyFieldDirty(node, week, field) {
      const current = this.parseWeekValue(this.weekDraft(node, week)[field])
      const saved = this.parseWeekValue(this.weekSaved(node, week)[field])
      return current !== saved
    },
    ensureWeekDraft(node, week) {
      if (!node.weeklyDraft) {
        this.$set(node, 'weeklyDraft', {})
      }
      if (!node.weeklyDraft[week]) {
        this.$set(node.weeklyDraft, week, {
          assigned_people: null,
          scheduled_quantity: null,
        })
      }
      if (!node.savedWeekly) {
        this.$set(node, 'savedWeekly', {})
      }
      if (!node.savingWeekly) {
        this.$set(node, 'savingWeekly', {})
      }
    },
    onWeeklyInput(node, week, field, event) {
      this.ensureWeekDraft(node, week)
      this.$set(node.weeklyDraft[week], field, this.parseWeekValue(event.target.value))
    },
    cancelWeeklyField(node, week, field) {
      this.ensureWeekDraft(node, week)
      const saved = this.parseWeekValue(this.weekSaved(node, week)[field])
      this.$set(node.weeklyDraft[week], field, saved)
    },
    weekDates(week) {
      const found = (this.projectWeeks || []).find((item) => Number(item.number) === Number(week))
      if (found && found.start && found.end) {
        return { start: found.start, end: found.end }
      }
      if (Number(week) === Number(this.semanaActual) && this.weekStartDate && this.weekEndDate) {
        return { start: this.weekStartDate, end: this.weekEndDate }
      }
      return null
    },
    applySavedWeekly(node, week, row) {
      this.ensureWeekDraft(node, week)
      const next = {
        assigned_people: this.parseWeekValue(row && row.assigned_people),
        scheduled_quantity: this.parseWeekValue(row && row.scheduled_quantity),
      }
      this.$set(node.weeklyDraft, week, next)
      this.$set(node.savedWeekly, week, { ...next })
    },
    async saveWeeklyWeek(node, week) {
      if (!this.projectId || this.isSavingWeek(node, week)) {
        return
      }
      const dates = this.weekDates(week)
      if (!dates) {
        this.$notify.error('error_saving')
        return
      }
      this.ensureWeekDraft(node, week)
      const draft = this.weekDraft(node, week)
      this.$set(node.savingWeekly, week, true)
      try {
        const { data } = await axios.put(
          `/api/v2/project/${this.projectId}/boq-roots/${node.id}/weekly-distributions`,
          {
            weeks: [{
              week_number: week,
              starts_on: dates.start,
              ends_on: dates.end,
              assigned_people: draft.assigned_people,
              scheduled_quantity: draft.scheduled_quantity,
            }],
          },
        )
        if (!(data && data.success)) {
          throw new Error('save_failed')
        }
        const list = Array.isArray(data.weekly_distributions) ? data.weekly_distributions : []
        const saved = list.find((item) => Number(item.week_number) === Number(week))
        this.applySavedWeekly(node, week, saved || {
          assigned_people: null,
          scheduled_quantity: null,
        })
        if (data.available_hours_per_week != null) {
          this.$set(node, 'availableHoursPerWeek', Number(data.available_hours_per_week) || null)
        }
        applyWeeklyManHoursFromApi(node, data)
        this.$set(node, 'availableManHoursPerWeek', { ...(node.availableManHoursPerWeek || {}) })
        this.$set(node, 'scheduledManHoursPerWeek', { ...(node.scheduledManHoursPerWeek || {}) })
        this.$notify.success('save_successful')
      } catch (error) {
        this.$notify.error('error_saving')
      } finally {
        this.$set(node.savingWeekly, week, false)
      }
    },
    /**
     * HH planif / disp / prog / real: solo mapas del API (PHP).
     * Tras editar efectivo/programado, los mapas se actualizan al guardar.
     */
    hhFor(week, node) {
      return weekHhFromApi(node, week)
    },
    /** Disciplina/subdisciplina: mapas roll-up del API (no suma hijos en Vue). */
    groupWeek(node, week) {
      return groupWeekFromApi(node, week)
    },
    childrenOf(nodeId) {
      return this.childrenByParent[nodeId] || []
    },
    groupBg() {
      if (this.depth <= 0) {
        return this.ROW_D_BG
      }
      if (this.depth === 1) {
        return '#eef4ef'
      }
      return '#f5f8f5'
    },
    discBg(w) {
      if (w === this.semanaActual) {
        return this.depth > 0 ? '#e5f2ea' : '#ddefe6'
      }
      return this.groupBg()
    },
    stageBg(w) {
      return w === this.semanaActual ? this.CUR_BG : '#f7f9fc'
    },
    hhRealColor(hh) {
      return hh && hh.hhReal != null && hh.hhDisp > 0 && hh.hhReal > hh.hhDisp
        ? '#c0392b'
        : '#2e9e5b'
    },
    numStyle(value, color, bg) {
      const missing = value == null || !Number.isFinite(value)
      const style = {
        color: missing ? '#c7ccd4' : (color || '#374151'),
        borderRight: '1px solid #e5e9f0',
      }
      if (bg) style.backgroundColor = bg
      return style
    },
    async ensureChildren(parentId) {
      if (
        this.loadedParentIds.includes(parentId)
        || this.loadingParentIds.includes(parentId)
        || !this.projectId
      ) {
        return
      }
      this.loadingParentIds = [...this.loadingParentIds, parentId]
      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/boq-roots`, {
          params: {
            parent_id: parentId,
            ...this.weeklyApiParams,
          },
        })
        const list = (data && data.success && data.boq_roots) ? data.boq_roots : []
        const mapped = list.map(mapBoqRootToWeeklyNode)
        this.$set(this.childrenByParent, parentId, mapped)
        if (!this.loadedParentIds.includes(parentId)) {
          this.loadedParentIds = [...this.loadedParentIds, parentId]
        }
        this.$emit('children-loaded', { parentId, nodes: mapped })
      } catch (error) {
        this.$set(this.childrenByParent, parentId, [])
        this.$notify.error('error_loading_data')
      } finally {
        this.loadingParentIds = this.loadingParentIds.filter((id) => id !== parentId)
      }
    },
  },
}
</script>

<style scoped>
.weekly-qty-expand {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: #1e4d33;
}
.weekly-qty-group-row--nested .weekly-qty-name-cell {
  box-shadow: inset 3px 0 0 #8faf9a;
  font-weight: 600;
}
.weekly-qty-name-cell--nested {
  box-shadow: inset 3px 0 0 #c5d4cb;
}
.sem-num {
  padding: 0.25rem 0.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  white-space: nowrap;
}
.fs-10 { font-size: 10px; }
.fs-11 { font-size: 11px; }
.fs-11-5 { font-size: 11.5px; }
.sticky-left {
  position: sticky;
  left: 0;
}
.weekly-qty-factor-k {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.15rem;
}
.weekly-qty-factor-k__actions {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.15rem;
}
.weekly-qty-factor-k__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  background: transparent;
}
.weekly-qty-factor-k__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.weekly-qty-factor-k__btn--save {
  color: #15803d;
  border-color: #86efac;
  background: #f0fdf4;
}
.weekly-qty-factor-k__btn--cancel {
  color: #b91c1c;
  border-color: #fca5a5;
  background: #fef2f2;
}
</style>
